export type DayRow = {
  ymd: string;
  uv: number;
  pv: number;
  launches: number;
  entry_views: number;
  dials: number;
  reward_ads: number;
  reward_ads_dial?: number;
  reward_ads_pin?: number;
  reward_ads_refresh?: number;
  new_users: number;
  searches: number;
  posts?: number;
  posts_dawn?: number;
  posts_morning?: number;
  posts_afternoon?: number;
  posts_evening?: number;
};

export type AnalyticsResp = {
  ok: boolean;
  miniprogram?: { id: number; name: string; public_id: string };
  server_time?: string;
  timezone?: string;
  tracking_since?: string;
  today?: DayRow;
  yesterday?: DayRow;
  summary?: Omit<DayRow, 'ymd'>;
  daily?: DayRow[];
  range?: { from: string; to: string; days: number };
};

export type MetricKey = keyof Omit<DayRow, 'ymd'>;

export type MetricDef = {
  key: MetricKey;
  label: string;
  short: string;
  hint: string;
  accent: string;
  icon: string;
  primary?: boolean;
};

export const METRICS: MetricDef[] = [
  {
    key: 'uv',
    label: '独立访客 UV',
    short: 'UV',
    hint: '当日去重访客（登录用户或设备指纹）',
    accent: 'from-sky-500 to-blue-600',
    icon: 'fa-users',
    primary: true,
  },
  {
    key: 'pv',
    label: '页面访问 PV',
    short: 'PV',
    hint: 'bootstrap、详情页等 API 触发的访问次数',
    accent: 'from-violet-500 to-purple-600',
    icon: 'fa-eye',
    primary: true,
  },
  {
    key: 'launches',
    label: '打开次数',
    short: '打开',
    hint: '小程序 bootstrap 加载次数，近似启动量',
    accent: 'from-teal-500 to-emerald-600',
    icon: 'fa-mobile-screen',
    primary: true,
  },
  {
    key: 'entry_views',
    label: '详情浏览',
    short: '详情',
    hint: '信息详情页加载次数',
    accent: 'from-indigo-500 to-indigo-700',
    icon: 'fa-file-lines',
    primary: true,
  },
  {
    key: 'dials',
    label: '拨号次数',
    short: '拨号',
    hint: '用户点击拨号并上报的次数',
    accent: 'from-amber-500 to-orange-600',
    icon: 'fa-phone',
  },
  {
    key: 'reward_ads',
    label: '激励视频完播',
    short: '激励',
    hint: '完整观看并成功兑换：拨号次数、置顶或刷新（合计）',
    accent: 'from-rose-500 to-pink-600',
    icon: 'fa-clapperboard',
  },
  {
    key: 'new_users',
    label: '新增用户',
    short: '新用户',
    hint: 'C 端首次登录注册的小程序用户',
    accent: 'from-cyan-500 to-teal-600',
    icon: 'fa-user-plus',
  },
  {
    key: 'searches',
    label: '搜索次数',
    short: '搜索',
    hint: '首页有效关键词搜索',
    accent: 'from-slate-600 to-slate-800',
    icon: 'fa-magnifying-glass',
  },
  {
    key: 'posts',
    label: '信息发布数',
    short: '发布',
    hint: 'C 端用户提交发布成功次数（含待审核）',
    accent: 'from-lime-500 to-green-600',
    icon: 'fa-pen-to-square',
  },
];

export const REWARD_AD_SUBMETRICS: {
  key: 'reward_ads_dial' | 'reward_ads_pin' | 'reward_ads_refresh';
  label: string;
  hint: string;
}[] = [
  { key: 'reward_ads_dial', label: '拨号', hint: '兑换拨号次数' },
  { key: 'reward_ads_pin', label: '置顶', hint: '兑换信息置顶' },
  { key: 'reward_ads_refresh', label: '刷新', hint: '兑换信息刷新' },
];

export const POST_SLOT_METRICS: {
  key: 'posts_dawn' | 'posts_morning' | 'posts_afternoon' | 'posts_evening';
  label: string;
  range: string;
  accent: string;
}[] = [
  { key: 'posts_dawn', label: '凌晨', range: '00:00–06:00', accent: 'bg-indigo-400' },
  { key: 'posts_morning', label: '上午', range: '06:00–12:00', accent: 'bg-sky-400' },
  { key: 'posts_afternoon', label: '下午', range: '12:00–18:00', accent: 'bg-amber-400' },
  { key: 'posts_evening', label: '晚上', range: '18:00–24:00', accent: 'bg-violet-400' },
];

export const PERIOD_OPTIONS = [
  { days: 7, label: '近 7 天' },
  { days: 14, label: '近 14 天' },
  { days: 30, label: '近 30 天' },
] as const;

export const CHART_BAR_AREA_PX = 160;
export const MOBILE_CHART_BAR_AREA_PX = 112;

/** 手机端默认展示的趋势指标 */
export const MOBILE_TREND_METRIC_KEYS: MetricKey[] = ['uv', 'pv', 'dials', 'reward_ads'];

export function formatNum(n: number): string {
  if (!Number.isFinite(n)) return '0';
  if (n >= 100000) return `${(n / 10000).toFixed(1)}万`;
  return n.toLocaleString('zh-CN');
}

export function deltaPercent(today: number, yesterday: number): number | null {
  if (yesterday <= 0) {
    return today > 0 ? 100 : null;
  }
  return Math.round(((today - yesterday) / yesterday) * 1000) / 10;
}

export function metricByKey(key: MetricKey): MetricDef {
  return METRICS.find((m) => m.key === key) ?? METRICS[0];
}
